/**
 * Module: lib/copilot/sharedRefresh
 * Responsibility: Single-flight dedup wrapper around the dashboard's existing
 *   `refreshExpiredIdToken(true)` so all copilot callsites that detect a 401
 *   share ONE refresh attempt at a time.
 *
 * Why: on a typical dashboard mount we fire several copilot requests in
 *   parallel (`/copilot/suggestions`, `/copilot/conversations`, possibly
 *   `/copilot/chat` shortly after). If the merchant's access token is
 *   expired, all of them will return 401 within milliseconds of each other.
 *   Calling `refreshExpiredIdToken(true)` from each handler independently
 *   creates a race: each refresh rotates the auth cookie + refresh-token
 *   cookie; whichever POST to /merchant-user/refresh wins gets new tokens,
 *   the others end up using a now-stale refresh token and 401, which
 *   `refreshExpiredIdToken` then escalates to a `redirectToLogin()` call.
 *   Net effect: a user who briefly had an expired token gets logged out
 *   instead of seamlessly recovering. Reported in PR #6765 review.
 *
 * Pattern: classic in-flight promise dedup. The first caller starts the
 *   refresh; subsequent callers awaiting while it's in flight get a
 *   reference to the same promise. After it resolves (success OR
 *   failure), the slot clears so the NEXT 401 wave starts a fresh refresh
 *   — we don't want a stale "true" result to lock us out of a real
 *   refresh attempt later.
 *
 * Concurrency: safe under JS's single-threaded event loop. The
 *   "check-then-assign" pattern around `inFlight` cannot race because no
 *   other tick can interleave between the `if (inFlight)` and the
 *   `inFlight = (async () => ...)()` lines.
 *
 * NOTE: `refreshExpiredIdToken` itself now single-flights the underlying
 *   network refresh (lib/utils/auth.ts), so the axios interceptor and
 *   pre-flight callers coalesce with copilot's too — the rotation race is
 *   closed across BOTH stacks, not just within copilot. This wrapper is kept
 *   as the copilot-facing import point; its local dedup is now a harmless
 *   extra layer.
 */

// Relative import (not the `src/lib/utils/auth` alias) so vitest can resolve
// this module from the __tests__ folder — the tsconfig path alias isn't
// reflected in the vitest resolver config.
import { refreshExpiredIdToken } from '../utils/auth'

/**
 * Module-level slot for the currently-running refresh. `null` when idle.
 * Exported for tests only — production code should call `refreshTokenDeduped`.
 */
let inFlight: Promise<boolean> | null = null

/**
 * Returns a promise that resolves to the same boolean
 * `refreshExpiredIdToken(true)` would resolve to, but coalesces concurrent
 * callers onto a single underlying refresh. Once the underlying call
 * settles (resolves or rejects), the slot is cleared so the next 401 wave
 * runs a fresh refresh.
 *
 * @returns true if the refresh succeeded and cookies were rotated,
 *          false if no refresh token was available or the refresh call
 *          itself failed silently. Note: `refreshExpiredIdToken` may
 *          additionally call `redirectToLogin()` on its own catch path —
 *          callers can't suppress that side-effect from here.
 */
export function refreshTokenDeduped(): Promise<boolean> {
  if (inFlight) {
    return inFlight
  }

  inFlight = (async () => {
    try {
      return await refreshExpiredIdToken(true)
    } finally {
      // Clear regardless of outcome so the next 401 wave can start fresh.
      // Doing this in `finally` (rather than `then`) also covers the case
      // where `refreshExpiredIdToken` throws — without this, a thrown
      // refresh would leave the slot held forever and block all future
      // refreshes for the lifetime of the page.
      inFlight = null
    }
  })()

  return inFlight
}

/**
 * Test-only escape hatch to clear the in-flight slot between test cases.
 * Module-level state otherwise leaks across `it()` blocks.
 *
 * NOT exported from any public surface — only used by Vitest specs that
 * import this file directly. Production code MUST NOT call this; doing so
 * defeats the dedup invariant and re-introduces the race.
 */
export function __resetForTests(): void {
  inFlight = null
}
